export function trimString(value) {
  return String(value ?? "").trim();
}

export function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function formatText(text, vars = {}) {
  return String(text ?? "").replace(/\{\s*\$([A-Za-z0-9_]+)\s*\}/g, (_match, name) => (
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : ""
  ));
}

function languageHelper() {
  const helper = globalThis.window?.__fishystuffLanguage;
  return helper && typeof helper.t === "function" ? helper : null;
}

export function translatePresetText(key, fallback, vars = {}) {
  const normalizedFallback = formatText(fallback, vars);
  const helper = languageHelper();
  if (!helper || !trimString(key)) {
    return normalizedFallback;
  }
  const translated = helper.t(key, vars);
  return !translated || translated === key ? normalizedFallback : translated;
}

export function presetItemKey(kind, id) {
  const normalizedKind = trimString(kind).toLowerCase();
  const normalizedId = trimString(id);
  return normalizedKind && normalizedId ? `${normalizedKind}:${normalizedId}` : "";
}

export function normalizePresetBase(value) {
  const source = isPlainObject(value) ? value : {};
  const kind = trimString(source.kind).toLowerCase();
  const id = trimString(source.id);
  if ((kind === "preset" || kind === "local" || kind === "fixed") && id) {
    return {
      kind,
      id,
      revisionId: trimString(source.revisionId),
    };
  }
  return { kind: "none", id: "", revisionId: "" };
}

export function presetFixedItems(contract) {
  const configured = typeof contract?.fixedPresets === "function"
    ? contract.fixedPresets()
    : contract?.fixedPresets;
  return (Array.isArray(configured) ? configured : [])
    .map((preset, index) => {
      const id = trimString(preset?.id) || `fixed-${index + 1}`;
      return {
        key: presetItemKey("fixed", id),
        kind: "fixed",
        group: "built-in",
        id,
        name: trimString(preset?.name || preset?.label) || id,
        revisionId: trimString(preset?.revisionId),
        payload: preset?.payload ?? null,
      };
    });
}

export function presetContractForWorkspace(contract, workspace, collectionKey = contract?.key) {
  if (!workspace || typeof workspace.fixedPresets !== "function") {
    return contract;
  }
  return {
    ...contract,
    fixedPresets: workspace.fixedPresets(collectionKey),
  };
}

export function presetSavedReferenceState(state) {
  if (isPlainObject(state?.savedReferences) && trimString(state.savedReferences.kind)) {
    return state.savedReferences;
  }
  return { kind: "uninitialized" };
}

export function presetSavedReferencesReady(state) {
  return ["missing", "ready"].includes(presetSavedReferenceState(state).kind);
}

export function presetSavedItems(state) {
  if (!presetSavedReferencesReady(state)) {
    return [];
  }
  return (Array.isArray(state?.presets) ? state.presets : [])
    .map((preset) => {
      const id = trimString(preset?.id);
      if (!id) {
        return null;
      }
      return {
        key: presetItemKey("preset", id),
        kind: "preset",
        group: "account",
        id,
        name: trimString(preset?.name) || id,
        revision: preset?.revision,
        revisionId: trimString(preset?.payloadRevisionId),
        payload: preset?.payload ?? null,
        createdAt: preset?.createdAt,
        updatedAt: preset?.updatedAt,
      };
    })
    .filter(Boolean);
}

export function presetLocalSourceState(state) {
  if (isPlainObject(state?.localPresetSources) && trimString(state.localPresetSources.kind)) {
    return state.localPresetSources;
  }
  return { kind: "hidden" };
}

export function presetLocalItems(state) {
  if (presetLocalSourceState(state).kind !== "ready") {
    return [];
  }
  return (Array.isArray(state?.localPresets) ? state.localPresets : [])
    .map((preset) => {
      const id = trimString(preset?.id);
      if (!id) return null;
      return {
        key: presetItemKey("local", id),
        kind: "local",
        group: "local",
        id,
        name: trimString(preset?.name) || id,
        revision: preset?.revision,
        revisionId: trimString(preset?.payloadRevisionId),
        payload: preset?.payload ?? null,
        createdAt: preset?.createdAt,
        updatedAt: preset?.updatedAt,
      };
    })
    .filter(Boolean);
}

function timestampValue(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  const parsed = Date.parse(String(value ?? ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

export function formatPresetTimestamp(value) {
  const timestamp = timestampValue(value);
  if (!timestamp) {
    return "";
  }
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(timestamp));
  } catch (_error) {
    return new Date(timestamp).toLocaleString();
  }
}

export function describePresetActionKind(value) {
  const normalized = trimString(value)
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
  if (!normalized) {
    return translatePresetText("presets.history.change", "Preset changed");
  }
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export function presetWorkspaceStateMessage(state) {
  switch (state?.kind) {
    case "loading":
    case "uninitialized":
      return translatePresetText("presets.state.loading", "Loading preset workspace…");
    case "conflict":
      return translatePresetText(
        "presets.state.conflict",
        "This preset changed in another tab. Review the latest version before trying again.",
      );
    case "invalid":
      return translatePresetText(
        "presets.state.invalid",
        "Stored preset data is invalid. It has been preserved for recovery.",
      );
    case "unsupportedVersion":
      return translatePresetText(
        "presets.state.unsupported",
        "These presets were created by a newer version of FishyStuff.",
      );
    case "unavailable":
      return trimString(state?.message || state?.reason)
        || translatePresetText("presets.state.unavailable", "Preset storage is currently unavailable.");
    default:
      return translatePresetText("presets.state.unavailable", "Preset workspace is not ready.");
  }
}

export function presetCommandErrorMessage(error) {
  return error instanceof Error && trimString(error.message)
    ? error.message
    : translatePresetText("presets.error.command", "The preset action could not be completed.");
}

export function readPresetWorkspaceState(workspace, collectionKey) {
  try {
    return workspace?.state?.(collectionKey) ?? {
      kind: "uninitialized",
      collectionKey,
    };
  } catch (error) {
    return {
      kind: "unavailable",
      collectionKey,
      reason: presetCommandErrorMessage(error),
    };
  }
}

function normalizePresetHistoryNodes(nodes) {
  return (Array.isArray(nodes) ? nodes : [])
    .filter((node) => trimString(node?.revisionId))
    .map((node) => ({
      ...node,
      revisionId: trimString(node.revisionId),
      parentRevisionId: trimString(node.parentRevisionId),
      childRevisionIds: Array.isArray(node.childRevisionIds)
        ? node.childRevisionIds.map(trimString).filter(Boolean)
        : [],
    }));
}

function newestPresetHistoryNode(nodes) {
  // The repository projection owns exact timestamp ordering, including
  // sub-millisecond precision that Date cannot represent.
  return nodes.at(-1) ?? null;
}

function presetHistoryPrimaryComponent(components, currentRevisionId, preferredRevisionId) {
  const contains = (component, revisionId) => revisionId
    && component.some((node) => node.revisionId === revisionId);
  return components.find((component) => contains(component, currentRevisionId))
    ?? components.find((component) => contains(component, preferredRevisionId))
    ?? components[0]
    ?? [];
}

function presetHistoryComponents(nodes, projectionOrder) {
  const byId = new Map(nodes.map((node) => [node.revisionId, node]));
  const linkedIds = new Map(nodes.map((node) => [node.revisionId, new Set()]));
  // Partition strictly by stored edges. The graph view may later add one
  // presentation-only base for components that share the same hidden parent.
  for (const node of nodes) {
    if (!node.parentRevisionId || !byId.has(node.parentRevisionId)) {
      continue;
    }
    linkedIds.get(node.revisionId).add(node.parentRevisionId);
    linkedIds.get(node.parentRevisionId).add(node.revisionId);
  }
  const visited = new Set();
  const components = [];
  for (const node of nodes) {
    if (visited.has(node.revisionId)) {
      continue;
    }
    const componentIds = new Set();
    const pending = [node.revisionId];
    while (pending.length > 0) {
      const revisionId = pending.pop();
      if (visited.has(revisionId)) {
        continue;
      }
      visited.add(revisionId);
      componentIds.add(revisionId);
      for (const linkedId of linkedIds.get(revisionId) ?? []) {
        pending.push(linkedId);
      }
    }
    components.push(nodes.filter((candidate) => componentIds.has(candidate.revisionId)));
  }
  return components.sort((left, right) => (
    (projectionOrder.get(newestPresetHistoryNode(right)?.revisionId) ?? -1)
    - (projectionOrder.get(newestPresetHistoryNode(left)?.revisionId) ?? -1)
  ));
}

function presetHistoryChildOrder(parent, left, right, projectionOrder) {
  if (Boolean(left.activePath) !== Boolean(right.activePath)) {
    return left.activePath ? -1 : 1;
  }
  return (projectionOrder.get(right.revisionId) ?? -1)
    - (projectionOrder.get(left.revisionId) ?? -1);
}

function markPresetHistorySegment(segment, key, active) {
  segment[key] = true;
  if (active) {
    segment[`${key}Active`] = true;
  }
}

function layoutPresetHistoryGraph(connectedNodes, projectionOrder) {
  if (connectedNodes.length === 0) {
    return { laneCount: 0, rows: [] };
  }
  const byId = new Map(connectedNodes.map((node) => [node.revisionId, node]));
  const childrenByParent = new Map(connectedNodes.map((node) => [node.revisionId, []]));
  for (const node of connectedNodes) {
    if (node.parentRevisionId && byId.has(node.parentRevisionId)) {
      childrenByParent.get(node.parentRevisionId).push(node);
    }
  }
  for (const parent of connectedNodes) {
    childrenByParent.get(parent.revisionId)
      .sort((left, right) => presetHistoryChildOrder(parent, left, right, projectionOrder));
  }

  const newestDescendantOrder = new Map();
  const subtreeHasActivePath = new Map();
  const inspectSubtree = (node, ancestors = new Set()) => {
    if (newestDescendantOrder.has(node.revisionId) || ancestors.has(node.revisionId)) {
      return;
    }
    const nextAncestors = new Set(ancestors);
    nextAncestors.add(node.revisionId);
    let newest = projectionOrder.get(node.revisionId) ?? -1;
    let active = Boolean(node.activePath);
    for (const child of childrenByParent.get(node.revisionId) ?? []) {
      inspectSubtree(child, nextAncestors);
      newest = Math.max(newest, newestDescendantOrder.get(child.revisionId) ?? -1);
      active ||= subtreeHasActivePath.get(child.revisionId) === true;
    }
    newestDescendantOrder.set(node.revisionId, newest);
    subtreeHasActivePath.set(node.revisionId, active);
  };
  for (const node of connectedNodes) {
    inspectSubtree(node);
  }
  const roots = connectedNodes
    .filter((node) => !node.parentRevisionId || !byId.has(node.parentRevisionId))
    .sort((left, right) => {
      const leftActive = subtreeHasActivePath.get(left.revisionId) === true;
      const rightActive = subtreeHasActivePath.get(right.revisionId) === true;
      if (leftActive !== rightActive) {
        return leftActive ? -1 : 1;
      }
      return (newestDescendantOrder.get(right.revisionId) ?? -1)
        - (newestDescendantOrder.get(left.revisionId) ?? -1);
    });
  const laneById = new Map();
  const visited = new Set();
  let laneCount = 0;
  // Leaves own stable lanes; each parent continues its preferred child's lane.
  // Other child lanes join it at that parent's row.
  const assignLanes = (node, ancestors = new Set()) => {
    if (visited.has(node.revisionId) || ancestors.has(node.revisionId)) {
      return laneById.get(node.revisionId) ?? null;
    }
    const nextAncestors = new Set(ancestors);
    nextAncestors.add(node.revisionId);
    const childLanes = (childrenByParent.get(node.revisionId) ?? [])
      .map((child) => assignLanes(child, nextAncestors))
      .filter((lane) => lane != null);
    const lane = childLanes[0] ?? laneCount++;
    laneById.set(node.revisionId, lane);
    visited.add(node.revisionId);
    return lane;
  };
  for (const root of roots) {
    assignLanes(root);
  }
  for (const node of connectedNodes) {
    assignLanes(node);
  }

  const rowById = new Map();
  const assignRows = (node, ancestors = new Set()) => {
    if (rowById.has(node.revisionId) || ancestors.has(node.revisionId)) {
      return rowById.get(node.revisionId) ?? 0;
    }
    const nextAncestors = new Set(ancestors);
    nextAncestors.add(node.revisionId);
    const childRows = (childrenByParent.get(node.revisionId) ?? [])
      .map((child) => assignRows(child, nextAncestors));
    const row = childRows.length > 0 ? Math.max(...childRows) + 1 : 0;
    rowById.set(node.revisionId, row);
    return row;
  };
  for (const root of roots) {
    assignRows(root);
  }
  for (const node of connectedNodes) {
    assignRows(node);
  }

  const visualRowCount = Math.max(0, ...rowById.values()) + 1;
  const visualLaneCount = Math.max(1, laneCount);
  const rows = Array.from({ length: visualRowCount }, () => ({
    nodes: [],
    passThrough: Array.from({ length: visualLaneCount }, () => ({})),
    connectorSegments: Array.from({ length: visualLaneCount }, () => ({})),
  }));
  for (const node of connectedNodes) {
    rows[rowById.get(node.revisionId) ?? 0].nodes.push({
      node,
      lane: laneById.get(node.revisionId) ?? 0,
    });
  }
  for (const row of rows) {
    row.nodes.sort((left, right) => left.lane - right.lane);
  }
  for (const child of connectedNodes) {
    const parent = byId.get(child.parentRevisionId);
    if (!parent) {
      continue;
    }
    const childRow = rowById.get(child.revisionId);
    const parentRow = rowById.get(parent.revisionId);
    if (childRow == null || parentRow == null || childRow >= parentRow) {
      continue;
    }
    const childLane = laneById.get(child.revisionId);
    const parentLane = laneById.get(parent.revisionId);
    const active = Boolean(child.activePath && parent.activePath);
    for (let rowIndex = childRow + 1; rowIndex < parentRow; rowIndex += 1) {
      rows[rowIndex].passThrough[childLane].present = true;
      rows[rowIndex].passThrough[childLane].active ||= active;
    }
    for (let gapIndex = childRow; gapIndex < parentRow - 1; gapIndex += 1) {
      markPresetHistorySegment(rows[gapIndex].connectorSegments[childLane], "top", active);
      markPresetHistorySegment(rows[gapIndex].connectorSegments[childLane], "bottom", active);
    }
    const mergeSegments = rows[parentRow - 1].connectorSegments;
    markPresetHistorySegment(mergeSegments[childLane], "top", active);
    markPresetHistorySegment(mergeSegments[parentLane], "bottom", active);
    const start = Math.min(childLane, parentLane);
    const end = Math.max(childLane, parentLane);
    for (let lane = start; lane <= end; lane += 1) {
      if (lane > start) {
        markPresetHistorySegment(mergeSegments[lane], "left", active);
      }
      if (lane < end) {
        markPresetHistorySegment(mergeSegments[lane], "right", active);
      }
    }
  }
  return {
    laneCount: visualLaneCount,
    rows,
  };
}

function presetHistoryNodesWithSharedBases(nodes, projectionOrder, currentRevisionId) {
  const visibleIds = new Set(nodes.map((node) => node.revisionId));
  const rootsByMissingParent = new Map();
  for (const node of nodes) {
    if (!node.parentRevisionId || visibleIds.has(node.parentRevisionId)) {
      continue;
    }
    const roots = rootsByMissingParent.get(node.parentRevisionId) ?? [];
    roots.push(node);
    rootsByMissingParent.set(node.parentRevisionId, roots);
  }
  const unified = [...nodes];
  for (const [revisionId, roots] of rootsByMissingParent) {
    if (roots.length < 2) {
      continue;
    }
    const earliestChildOrder = Math.min(
      ...roots.map((root) => projectionOrder.get(root.revisionId) ?? 0),
    );
    unified.push({
      revisionId,
      parentRevisionId: "",
      childRevisionIds: roots.map((root) => root.revisionId),
      graphBase: true,
      activePath: revisionId === currentRevisionId,
      changes: [],
    });
    projectionOrder.set(revisionId, earliestChildOrder - 0.5);
  }
  return unified;
}

export function buildPresetHistoryView(nodes, {
  currentRevisionId = "",
  preferredRevisionId = "",
  showAlternateBranches = false,
} = {}) {
  const normalizedNodes = normalizePresetHistoryNodes(nodes);
  const projectionOrder = new Map(
    normalizedNodes.map((node, index) => [node.revisionId, index]),
  );
  const components = presetHistoryComponents(normalizedNodes, projectionOrder);
  const currentId = trimString(currentRevisionId);
  const primaryComponent = presetHistoryPrimaryComponent(
    components,
    currentId,
    trimString(preferredRevisionId),
  );
  const alternatesAvailable = components.length > 1;
  const alternatesShown = alternatesAvailable && showAlternateBranches === true;
  const displayedNodes = alternatesShown
    ? presetHistoryNodesWithSharedBases(normalizedNodes, projectionOrder, currentId)
    : primaryComponent;
  return {
    graph: layoutPresetHistoryGraph(displayedNodes, projectionOrder),
    alternates: {
      available: alternatesAvailable,
      shown: alternatesShown,
      primaryRevisionId: newestPresetHistoryNode(primaryComponent)?.revisionId ?? "",
    },
  };
}

export function buildPresetHistoryGraph(nodes, currentRevisionId = "") {
  return buildPresetHistoryView(nodes, { currentRevisionId }).graph;
}

function presetChangePath(parent, key, arrayIndex = false) {
  if (arrayIndex) {
    return `${parent}[${key}]`;
  }
  const property = String(key);
  return /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(property)
    ? `${parent}.${property}`
    : `${parent}[${JSON.stringify(property)}]`;
}

function collectPresetPayloadChanges(
  changes,
  saved,
  working,
  path,
  pathSegments,
  savedPresent,
  workingPresent,
  limit,
) {
  if (changes.length >= limit) {
    return;
  }
  if (savedPresent && workingPresent && Object.is(saved, working)) {
    return;
  }
  if (savedPresent && workingPresent && Array.isArray(saved) && Array.isArray(working)) {
    const length = Math.max(saved.length, working.length);
    for (let index = 0; index < length; index += 1) {
      collectPresetPayloadChanges(
        changes,
        saved[index],
        working[index],
        presetChangePath(path, index, true),
        [...pathSegments, { kind: "index", index }],
        Object.prototype.hasOwnProperty.call(saved, index),
        Object.prototype.hasOwnProperty.call(working, index),
        limit,
      );
    }
    return;
  }
  if (savedPresent && workingPresent && isPlainObject(saved) && isPlainObject(working)) {
    const keys = [...new Set([...Object.keys(saved), ...Object.keys(working)])].sort();
    for (const key of keys) {
      collectPresetPayloadChanges(
        changes,
        saved[key],
        working[key],
        presetChangePath(path, key),
        [...pathSegments, { kind: "property", name: key }],
        Object.prototype.hasOwnProperty.call(saved, key),
        Object.prototype.hasOwnProperty.call(working, key),
        limit,
      );
    }
    return;
  }
  changes.push({
    kind: !savedPresent ? "added" : !workingPresent ? "removed" : "changed",
    path,
    pathSegments,
    savedPresent,
    savedValue: saved,
    workingPresent,
    workingValue: working,
  });
}

export function buildPresetPayloadChanges(savedPayload, workingPayload, limit = Number.POSITIVE_INFINITY) {
  const changes = [];
  collectPresetPayloadChanges(
    changes,
    savedPayload,
    workingPayload,
    "$",
    [],
    true,
    true,
    Number.isFinite(limit) ? Math.max(0, Math.floor(limit)) : Number.POSITIVE_INFINITY,
  );
  return changes;
}

function presetChangeWithPresentation(change, contract) {
  let presentation = null;
  try {
    presentation = typeof contract?.presentChange === "function"
      ? contract.presentChange(change)
      : null;
  } catch (_error) {
    presentation = null;
  }
  return presentation ? { ...change, presentation } : change;
}

export function presetHistoryNodesForItem(state, item, contract) {
  if (!item) {
    return [];
  }
  const nodes = Array.isArray(state?.history?.nodes) ? state.history.nodes : [];
  const nodesById = new Map(nodes.map((node) => [trimString(node?.revisionId), node]));
  return nodes
    .filter((node) => {
      const basedOn = normalizePresetBase(node?.basedOn);
      return basedOn.kind === item.kind && basedOn.id === item.id;
    })
    .map((node) => {
      const parent = nodesById.get(trimString(node?.parentRevisionId)) ?? null;
      const parentPayload = parent?.payload ?? node?.parentPayload;
      const hasParentPayload = parentPayload !== undefined && parentPayload !== null;
      const payloadsMatch = hasParentPayload
        && typeof contract?.payloadsEqual === "function"
        && contract.payloadsEqual(parentPayload, node.payload) === true;
      const projectedChanges = hasParentPayload && !payloadsMatch
        ? buildPresetPayloadChanges(parentPayload, node.payload, 4)
        : [];
      return {
        ...node,
        changes: projectedChanges.slice(0, 3).map((change) => (
          presetChangeWithPresentation(change, contract)
        )),
        changesTruncated: projectedChanges.length > 3,
      };
    });
}

export function buildPresetManagerModel(
  state,
  contract,
  selectedKey = "",
  historyOptions = {},
) {
  const fixed = presetFixedItems(contract);
  const savedReferences = presetSavedReferenceState(state);
  const savedReferencesReady = presetSavedReferencesReady(state);
  const savedReferencesMessage = savedReferencesReady
    ? ""
    : presetWorkspaceStateMessage(savedReferences);
  if (state?.kind !== "ready") {
    return {
      ready: false,
      state,
      savedReferences,
      savedReferencesReady,
      savedReferencesMessage,
      fixed,
      presets: [],
      localPresetSources: presetLocalSourceState(state),
      localPresets: [],
      items: fixed,
      selectedKey: "",
      selectedItem: null,
      baseItem: null,
      current: null,
      selectedHistoryGraph: { laneCount: 0, rows: [] },
      selectedHistoryAlternates: { available: false, shown: false, primaryRevisionId: "" },
      selectedChanges: [],
      selectedChangesTruncated: false,
      selectedIsActive: false,
    };
  }
  const presets = presetSavedItems(state);
  const localPresetSources = presetLocalSourceState(state);
  const localPresets = presetLocalItems(state);
  const items = [...fixed, ...presets, ...localPresets];
  const current = state.current ?? null;
  const basedOn = normalizePresetBase(current?.basedOn);
  const baseKey = presetItemKey(basedOn.kind, basedOn.id);
  const storedBaseNode = !savedReferencesReady && basedOn.kind === "preset"
    ? (Array.isArray(state?.history?.nodes) ? state.history.nodes : []).find((node) => (
        trimString(node?.revisionId) === basedOn.revisionId
        || (
          normalizePresetBase(node?.basedOn).kind === "preset"
          && normalizePresetBase(node?.basedOn).id === basedOn.id
          && normalizePresetBase(node?.basedOn).revisionId === basedOn.revisionId
          && node?.parentPayload != null
        )
      )) ?? null
    : null;
  const storedBasePayload = trimString(storedBaseNode?.revisionId) === basedOn.revisionId
    ? storedBaseNode?.payload
    : storedBaseNode?.parentPayload;
  const ephemeralBaseItem = storedBaseNode && storedBasePayload != null
    ? {
        key: baseKey,
        kind: "preset",
        id: basedOn.id,
        name: translatePresetText(
          "presets.current.unnamed",
          "Unnamed preset",
        ),
        revisionId: basedOn.revisionId,
        payload: storedBasePayload,
        ephemeral: true,
      }
    : null;
  const baseItem = items.find((item) => item.key === baseKey) ?? ephemeralBaseItem;
  const requested = trimString(selectedKey);
  const nextSelectedKey = items.some((item) => item.key === requested)
    ? requested
    : baseItem
      ? baseKey
      : presets[0]?.key || fixed[0]?.key || localPresets[0]?.key || "";
  const selectedItem = items.find((item) => item.key === nextSelectedKey)
    ?? (baseItem?.key === nextSelectedKey ? baseItem : null);
  const selectedHistoryNodes = presetHistoryNodesForItem(state, selectedItem, contract);
  const selectedIsActive = Boolean(selectedItem && selectedItem.key === baseKey);
  const selectedPayloadMatches = selectedItem && current && typeof contract?.payloadsEqual === "function"
    ? contract.payloadsEqual(selectedItem.payload, current.payload) === true
    : false;
  const projectedChanges = selectedIsActive && current?.modified === true && !selectedPayloadMatches
    ? buildPresetPayloadChanges(selectedItem.payload, current.payload, 201)
    : [];
  const selectedHistoryView = buildPresetHistoryView(selectedHistoryNodes, {
    currentRevisionId: selectedIsActive ? current?.revisionId : "",
    preferredRevisionId: historyOptions?.preferredRevisionId,
    showAlternateBranches: historyOptions?.showAlternateBranches,
  });
  return {
    ready: true,
    state,
    savedReferences,
    savedReferencesReady,
    savedReferencesMessage,
    fixed,
    presets,
    localPresetSources,
    localPresets,
    items,
    selectedKey: nextSelectedKey,
    selectedItem,
    baseItem,
    basedOn,
    current,
    selectedHistoryGraph: selectedHistoryView.graph,
    selectedHistoryAlternates: selectedHistoryView.alternates,
    selectedChanges: projectedChanges.slice(0, 200).map((change) => (
      presetChangeWithPresentation(change, contract)
    )),
    selectedChangesTruncated: projectedChanges.length > 200,
    selectedIsActive,
  };
}

export function presetDiscardItem(model) {
  if (!model?.ready || model.current?.modified !== true) {
    return null;
  }
  return model.baseItem
    ?? model.fixed?.find((item) => item.id === "default")
    ?? model.fixed?.[0]
    ?? null;
}
